// CalendarView.jsx

import React, { useCallback, useMemo, useRef, useState } from 'react';

// Context
import { useCalendarTimeline } from 'context/CalendarTimelineContext';
import { useUser } from 'context/UserContext';

// Hooks
import useCalendarEventMove from './hooks/useCalendarEventMove';
import useNodeOpenIntent from 'views/hooks/useNodeOpenIntent';

// CSS
import './CalendarView.css';

// Components
import AgendaView from './views/agenda/AgendaView';
import DayView from './views/day/DayView';
import MonthView from './views/month/MonthView';
import ThreeDayView from './views/threeday/ThreeDayView';
import WeekView from './views/week/WeekView';
import RecurringOccurrenceChooser, {
    hasRecurrence,
} from './RecurringOccurrenceChooser';
import {
    buildInitialFieldsForCalendarDate,
    buildTemporalValueForField,
    getDefaultTemporalFieldKey,
    resolveCalendarCreateField,
} from 'views/calendar/shared/calendarTemporalFields';

const CALENDAR_VIEW_COMPONENTS = {
    MONTH: MonthView,
    WEEK: WeekView,
    THREEDAY: ThreeDayView,
    DAY: DayView,
    AGENDA: AgendaView,
};

/**
 * CalendarView
 * 
 * Renders a calendar view.
 * 
 * @param {object} view - The view object containing type and other properties.
 * @param {object} typeDef - The type definition for the nodes.
 * @param {object[]} nodes - The array of nodes to display.
 * @param {string} parentNodeKey - The key of the parent node, if applicable.
 * @param {string} parentNodeTypeLabel - The type label of the parent node, used for contextual actions.
 * @param {string} language - The current language code.
 * @param {string} addTypeKey - The type key to use when creating a new node from a quick action, if applicable.
 * @param {function} onQuickActionSelect - Callback function to call when a quick action is selected, called with (typeKey, options).
 * @param {function} onQuickActionCreateOnly - Callback function to call when a quick action is triggered without selection, called with (typeKey, options).
 * @param {function} onCalendarAddRequest - Callback when type is unknown; called with (date, { includeTime }).
 *
 * @returns {ReactNode} - CalendarView.
 */
const CalendarView = React.memo(({
    view,
    typeDef,
    nodes = [],
    eventItems,
    parentNodeKey = null,
    parentNodeTypeLabel = '',
    language = 'en',
    addTypeKey,
    onQuickActionCreateOnly,
    onQuickActionSelect,
    onCalendarAddRequest = null,
    interactionMode = 'base',
    onEventMoved = null,
    onItemClick = null,
}) => {

    // ----- CONTEXT ----- //

    const { calendarViewType } = useCalendarTimeline();
    const { setDisplayedUser } = useUser();

    // ----- HOOKS ----- //

    const { openNode } = useNodeOpenIntent();
    const [seriesChooserItem, setSeriesChooserItem] = useState(null);

    const {
        applyOptimisticEventItems,
        applyOptimisticNodes,
        canDragEvents,
        dragOverTargetKey,
        draggingItem,
        handleDropTargetDragOver,
        handleEventDragEnd,
        handleEventDragStart,
        handleMoveToTarget,
    } = useCalendarEventMove({ typeDef, interactionMode, onEventMoved });

    // ----- MEMOS ----- //

    const displayNodes = useMemo(
        () => applyOptimisticNodes(nodes),
        [applyOptimisticNodes, nodes],
    );

    const displayEventItems = useMemo(
        () => applyOptimisticEventItems(eventItems),
        [applyOptimisticEventItems, eventItems],
    );

    const enableEventDrag = canDragEvents && calendarViewType !== 'AGENDA';

    const calendarMoveProps = useMemo(() => ({
        canDragEvents: enableEventDrag,
        draggingItem,
        dragOverTargetKey,
        onEventDragStart: handleEventDragStart,
        onEventDragEnd: handleEventDragEnd,
        onDropTargetDragOver: handleDropTargetDragOver,
        onMoveToTarget: handleMoveToTarget,
    }), [
        dragOverTargetKey,
        draggingItem,
        enableEventDrag,
        handleDropTargetDragOver,
        handleEventDragEnd,
        handleEventDragStart,
        handleMoveToTarget,
    ]);

    const calendarCreateFieldResolution = useMemo(
        () => resolveCalendarCreateField(typeDef, {
            preferredFieldKey: view?.calendarField,
        }),
        [typeDef, view?.calendarField],
    );
    const temporalFields = calendarCreateFieldResolution.fields;
    const createTargetField = calendarCreateFieldResolution.targetField;

    // ----- REFS ----- //

    const containerRef = useRef(null);

    // ----- HANDLERS ----- //

    const handleOpenNode = useCallback((node) => {
        if (!node) return;
        if (typeof onItemClick === 'function') {
            onItemClick(node);
            return;
        }
        if (node.type === 'USER') {
            setDisplayedUser(node);
        }
        openNode(node, { preferPeek: true });
    }, [onItemClick, openNode, setDisplayedUser]);

    const resolveEventItem = useCallback((nodeOrItem) => {
        if (!nodeOrItem) return null;
        if (nodeOrItem.node) return nodeOrItem;
        return { node: nodeOrItem };
    }, []);

    const handleNodeClick = useCallback((event, nodeOrItem) => {
        event?.preventDefault?.();
        const item = resolveEventItem(nodeOrItem);
        if (!item?.node) return;
        if (hasRecurrence(item)) {
            setSeriesChooserItem(item);
            return;
        }
        handleOpenNode(item.node);
    }, [handleOpenNode, resolveEventItem]);

    const handleNodeDoubleClick = useCallback((event, nodeOrItem) => {
        event?.preventDefault?.();
        const item = resolveEventItem(nodeOrItem);
        if (!item?.node) return;
        if (hasRecurrence(item)) {
            setSeriesChooserItem(item);
            return;
        }
        handleOpenNode(item.node);
    }, [handleOpenNode, resolveEventItem]);

    const handleCalendarQuickAdd = useCallback((date, { includeTime = false } = {}) => {
        if (addTypeKey) {
            const handler = onQuickActionSelect || onQuickActionCreateOnly;
            if (!handler) return;

            if (createTargetField) {
                const targetFieldKey = createTargetField?.name || createTargetField?.key;
                const initialFields = buildInitialFieldsForCalendarDate(typeDef, date, {
                    includeTime,
                    preferredFieldKey: targetFieldKey,
                });
                if (!initialFields) return;

                handler(addTypeKey, {
                    shouldNavigate: false,
                    initialFields,
                });
                return;
            }

            if (temporalFields.length < 2) return;

            const dateFieldOptions = temporalFields
                .map((field) => {
                    const fieldKey = field?.name || field?.key;
                    const fieldType = String(field?.type || '').toLowerCase();
                    if (!fieldKey || !fieldType) return null;

                    const value = buildTemporalValueForField({ fieldType, date, includeTime });
                    if (!value) return null;

                    return {
                        fieldKey,
                        fieldLabel: field?.label || '',
                        fieldType,
                        value,
                    };
                })
                .filter(Boolean);

            handler(addTypeKey, {
                quickAdd: true,
                shouldNavigate: false,
                initialFields: null,
                quickAddConfig: {
                    dateFieldOptions,
                    defaultDateFieldKey: getDefaultTemporalFieldKey(dateFieldOptions),
                },
            });
            return;
        }

        if (typeof onCalendarAddRequest === 'function') {
            onCalendarAddRequest(date, { includeTime });
        }
    }, [
        addTypeKey,
        createTargetField,
        onCalendarAddRequest,
        onQuickActionCreateOnly,
        onQuickActionSelect,
        temporalFields,
        typeDef,
    ]);
    const ActiveView = CALENDAR_VIEW_COMPONENTS[calendarViewType];

    // ----- RENDER ----- //

    return (
        <div className="calendar-view-container" ref={containerRef}>
            {ActiveView ? (
                <ActiveView
                  view={view}
                  typeDef={typeDef}
                  nodes={displayNodes}
                  eventItems={displayEventItems}
                  language={language}
                  {...calendarMoveProps}
                  onAddDate={(date) => handleCalendarQuickAdd(date, { includeTime: false })}
                  onAddDateTime={(dateTime) => handleCalendarQuickAdd(dateTime, { includeTime: true })}
                  onNodeClick={handleNodeClick}
                  onNodeDoubleClick={handleNodeDoubleClick}
                />
            ) : (
                calendarViewType
            )}
            <RecurringOccurrenceChooser
              item={seriesChooserItem}
              typeDef={typeDef}
              onClose={() => setSeriesChooserItem(null)}
              onOpenSeries={handleOpenNode}
            />
        </div>
    );

});

// Dev Tools
CalendarView.displayName = 'CalendarView';

export default CalendarView;
